import { useCallback, useMemo } from 'react';
import type { PricingAddOn, PricingCard, PricingPromotion } from '@ecobox/domain';
import {
  livePromotions,
  offeredAddOns,
  selectPricingCards,
  suggestSeasons,
  watchConfigList,
  type City,
  type ConfigEntry,
  type Coverage,
} from '@ecobox/firebase';
import type { ConfigKindName, ConfigRecord } from '@ecobox/schema';
import { useServices } from '../services';
import { useWatch } from './useWatch';

type BoxSize = ConfigRecord<'boxSize'>;
const NONE: never[] = [];

export function useConfigRecords<K extends ConfigKindName>(
  kind: K,
  enabled: boolean,
): { records: ConfigRecord<K>[]; loading: boolean } {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: ConfigEntry<K>[]) => void, e: (err: Error) => void) => watchConfigList(db, kind, d, e),
    [db, kind],
  );
  const state = useWatch(enabled ? start : null, NONE as ConfigEntry<K>[]);
  const records = useMemo(() => state.data.map((e) => e.record), [state.data]);
  return { records, loading: enabled && state.loading };
}

export interface PickupConfig {
  /** Prices, sizes and cities are on this phone (or arriving). False until a pickup run needs them. */
  enabled: boolean;
  loading: boolean;
  cities: City[];
  cityMap: ReadonlyMap<string, City>;
  coverage: ReadonlyMap<string, Coverage>;
  sizes: BoxSize[];
  sizeMap: ReadonlyMap<string, BoxSize>;
  branches: ReadonlyMap<string, ConfigRecord<'branch'>>;
  sectors: ReadonlyMap<string, ConfigRecord<'sector'>>;
  /** The routes (D-28): which services a destination country has from the branch's country. */
  routes: ConfigRecord<'route'>[];
  /** Cards that price one service at one branch, branch card first (03 §3.3). */
  cardsFor(args: {
    branchId: string;
    parentHubId?: string | undefined;
    sectorId: string | undefined;
    service: string;
    today: string;
  }): PricingCard[];
  addOnsFor(cards: readonly PricingCard[]): readonly PricingAddOn[];
  /** Active promotions not yet ended on `today` (03 §3.3); the quote picks the one that applies. */
  promotionsFor(today: string): PricingPromotion[];
  suggestedSeasons(today: string): string[];
  /** Price of an empty box by size at one branch (for boxes sold with the shipment). */
  emptyPrices(branchId: string): Map<string, number>;
}

/**
 * The configuration a pickup needs to price a shipment on the phone: rate cards, box sizes,
 * destination cities and coverage, branches and countries (VAT, currency), seasons and empty-box
 * prices (02 §2.2, driver working set). Staff may read all of it (07 §7.3). Read while online once
 * a pickup run is on the phone, so it is in the Firestore cache when the signal drops.
 */
export function usePickupConfig(enabled: boolean): PickupConfig {
  const cities = useConfigRecords('destinationCity', enabled);
  const coverage = useConfigRecords('coverage', enabled);
  const sizes = useConfigRecords('boxSize', enabled);
  const cards = useConfigRecords('rateCard', enabled);
  const seasons = useConfigRecords('season', enabled);
  const empty = useConfigRecords('emptyBoxPrice', enabled);
  const branches = useConfigRecords('branch', enabled);
  const sectors = useConfigRecords('sector', enabled);
  const routes = useConfigRecords('route', enabled);
  const promotions = useConfigRecords('promotion', enabled);

  return useMemo<PickupConfig>(() => {
    const activeCities = cities.records.filter((c) => c.active);
    const activeSizes = sizes.records
      .filter((s) => s.active)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
    const activeSeasons = seasons.records.filter((s) => s.active);
    const cardsFor: PickupConfig['cardsFor'] = (args) => selectPricingCards(cards.records, args);
    return {
      enabled,
      loading:
        cities.loading ||
        sizes.loading ||
        cards.loading ||
        branches.loading ||
        sectors.loading ||
        routes.loading ||
        promotions.loading,
      cities: activeCities,
      cityMap: new Map(cities.records.map((c) => [c.id, c])),
      coverage: new Map(coverage.records.map((c) => [c.cityId, c])),
      sizes: activeSizes,
      sizeMap: new Map(sizes.records.map((s) => [s.code, s])),
      branches: new Map(branches.records.map((b) => [b.code, b])),
      sectors: new Map(sectors.records.map((s) => [s.code, s])),
      routes: routes.records,
      cardsFor,
      addOnsFor: offeredAddOns,
      promotionsFor: (today) => livePromotions(promotions.records, today),
      suggestedSeasons: (today) => suggestSeasons(activeSeasons, today),
      emptyPrices: (branchId) =>
        new Map(
          empty.records.filter((r) => r.branchId === branchId).map((r) => [r.sizeId, r.priceMinor]),
        ),
    };
  }, [
    enabled,
    cities.records,
    cities.loading,
    coverage.records,
    sizes.records,
    sizes.loading,
    cards.records,
    cards.loading,
    seasons.records,
    empty.records,
    branches.records,
    branches.loading,
    promotions.records,
    promotions.loading,
    sectors.records,
    sectors.loading,
    routes.records,
    routes.loading,
  ]);
}
